import { useMount } from "react-use";

interface useSeedFirstSegmentArgs {
  firstSegmentInitialKeystroke: string | null | undefined;
  firstSegmentRef: React.RefObject<HTMLElement | null> | undefined;
}

/**
 * Applies an initial keystroke to the first date segment in a date field by dispatching a native
 * `beforeinput` event with `inputType: "insertText"`.
 */
export function useSeedFirstSegment({ firstSegmentInitialKeystroke, firstSegmentRef }: useSeedFirstSegmentArgs): void {
  useMount(() => {
    if (firstSegmentInitialKeystroke == null || firstSegmentRef?.current == null) {
      return;
    }

    const firstSegment = firstSegmentRef.current;

    firstSegment.focus();
    // (jullman): We must use an `InputEvent` of type `beforeinput` as opposed to a simple keyboard
    // event in order to formalize the input and trigger subsequent handling. Otherwise, the first
    // keystroke is passed through, but is overwritten by the next one.
    const frameId = requestAnimationFrame(() => {
      const firstKeystrokeEvent = new InputEvent(
        "beforeinput" as const,
        {
          bubbles: true,
          cancelable: true,
          composed: true,
          data: firstSegmentInitialKeystroke,
          inputType: "insertText" as const,
        } as InputEventInit,
      );
      firstSegment.dispatchEvent(firstKeystrokeEvent);
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  });
}
